import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi } from '../api';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already authenticated (session cookie)
  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      const res = await authApi.get('/auth/me');
      if (res.data.authenticated) {
        setUser(res.data.user);
      } else {
        setUser(null);
      }
      setError(null);
    } catch (err) {
      // 401 is expected when not logged in
      if (err.response && err.response.status === 401) {
        setUser(null);
      } else {
        console.error('Auth check failed:', err);
        setError('Unable to verify authentication.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Check URL for auth errors from the callback redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('authError');
    if (authError) {
      setError(authError);
      // Remove only the authError param, preserve others
      params.delete('authError');
      const remaining = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (remaining ? '?' + remaining : ''));
    }
  }, []);

  const login = (returnTo = '/') => {
    // Redirect to the server's Azure AD login endpoint
    window.location.href = `/auth/login?returnTo=${encodeURIComponent(returnTo)}`;
  };

  const logout = async () => {
    try {
      const res = await authApi.post('/auth/logout', {
        redirectTo: window.location.origin,
      });

      if (res.data.logoutUrl) {
        window.location.href = res.data.logoutUrl;
      } else {
        setUser(null);
        window.location.href = '/';
      }
    } catch (err) {
      console.error('Logout failed:', err);
      setUser(null);
      window.location.href = '/';
    }
  };

  return (
    <AuthContext.Provider value={{ user, loading, error, login, logout, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

export default AuthContext;
